const { MODERATION_VALUES, SENDING_TYPES, SENDING_STATUS } = require('../constants')

const Std = require('./Std')
const Conditions = require('./Conditions')
const Fallbacks = require('./Fallbacks')
const { numbersToIcons } = require('./numbersToIcons')
const { numbersToText } = require('./numbersToText')

class Actions {
  static get validatorRules() {
    const rules = {}

    for (const key in this.tagsMeta) {
      const { tag, valueValidator } = this.tagsMeta[key]

      rules[tag] = {
        ...valueValidator,
        optional: true
      }
    }

    return rules
  }

  static get tagsMeta() {
    return {
      text: {
        tag: 'text',
        valueValidator: {
          type: 'array',
          items: 'string',
          min: 1
        }
      },
      sender: {
        tag: 'sender',
        valueValidator: {
          type: 'array',
          items: 'string',
          min: 1
        }
      },
      moderation: {
        tag: 'moderation',
        valueValidator: {
          type: 'string',
          enum: MODERATION_VALUES
        }
      },
      type: {
        tag: 'type',
        valueValidator: {
          type: 'string',
          enum: SENDING_TYPES
        }
      },
      fallbacks: {
        tag: 'fallbacks',
        valueValidator: {
          type: 'array',
          items: 'object',
          min: 1
        }
      },
      buttons: {
        tag: 'buttons',
        valueValidator: {
          type: 'array',
          min: 1,
          items: {
            type: 'object',
            props: {
              text: 'string',
              action: 'string'
            }
          }
        }
      }
    }
  }

  static get tagsOnly() {
    return Object.keys(this.tagsMeta).map(key => this.tagsMeta[key].tag)
  }

  static validateValue(value, tag) {
    let result = false

    switch (tag) {
      case this.tagsMeta.sender.tag:
      case this.tagsMeta.text.tag: {
        if (Array.isArray(value) && value.length && value.every(str => typeof str === 'string')) {
          result = true
        }
        break
      }
      case this.tagsMeta.type.tag: {
        if (~SENDING_TYPES.indexOf(value)) {
          result = true
        }
        break
      }
      case this.tagsMeta.fallbacks.tag: {
        if (Array.isArray(value) && value.length) {
          result = true
        }
        break
      }
      case this.tagsMeta.moderation.tag: {
        if (~MODERATION_VALUES.indexOf(value)) {
          result = true
        }
        break
      }
      case this.tagsMeta.buttons.tag: {
        if (Array.isArray(value) && value.length) {
          result = true
        }
        break
      }
    }

    return result
  }

  static filterActionsObject(actions = {}) {
    const result = {}

    if (typeof actions === 'object' && !!actions) {
      for (const tag of this.tagsOnly) {
        let value = actions[tag]
        switch (tag) {
          case 'fallbacks': {
            value = Fallbacks.applyOptionsDefaultsToFallbacks(Fallbacks.clean(value, { keepCommonId: false }))
            break
          }
        }
        if (value !== undefined && this.validateValue(value, tag)) {
          result[tag] = value
        }
      }
    }

    return result
  }

  static async applyActionsToSending(sending, actions = {}, conditions = []) {
    const originalSender = sending.payload.sender
    const originalText = sending.payload.text
    let textReplaceRE
    async function getTextReplaceRE() {
      if (textReplaceRE) {
        return textReplaceRE
      }

      const textCondition = conditions.find(condition => condition.tag === 'text') || { value: '/.*/' }
      textReplaceRE = await Conditions.generateRegExpFromString(textCondition.value)

      return textReplaceRE
    }

    // Сортируем так, чтобы type отработал раньше остальных
    const tags = Object.keys(actions).sort((a, b) => {
      if (a === 'tagsMeta.type.tag') {
        return -1
      } else if (b === 'tagsMeta.type.tag') {
        return 1
      }

      return 0
    })

    for (const tag of tags) {
      let value = actions[tag]

      switch (tag) {
        case this.tagsMeta.sender.tag: {
          if (!Array.isArray(value)) {
            value = [ value ]
          }

          value = value.filter(v => typeof v === 'string' && v)

          if (value.length) {
            sending.payload.sender = value[Std.Math.randomBetween(0, value.length - 1)]
          }
          break
        }
        case this.tagsMeta.text.tag: {
          if (Array.isArray(value) && value.length) {
            value = value.filter(v => typeof v === 'string')
            value = value[Std.Math.randomBetween(0, value.length - 1)]
          }

          sending.payload.text = sending.payload.text.replace(await getTextReplaceRE(), value)
          sending.payload.text = sending.payload.text.replace('$sendername', originalSender)
          sending.payload.text = sending.payload.text.replace('$specialNumbers', () => numbersToIcons(originalText))
          sending.payload.text = sending.payload.text.replace('$textNumbers', () => numbersToText(originalText))
          break
        }
        case this.tagsMeta.moderation.tag: {
          if (~MODERATION_VALUES.indexOf(value)) {
            sending.state.moderation = value
            if (value === MODERATION_VALUES.rejected) {
              sending.state.status = SENDING_STATUS.failed
            }
          }
          break
        }
        case this.tagsMeta.type.tag: {
          if (~SENDING_TYPES.indexOf(value)) {
            sending.type = value
          }
          break
        }
        case this.tagsMeta.fallbacks.tag: {
          if (Array.isArray(value) && value.length) {
            sending.fallbacks = await Promise.all(value.map(async fallback => {
              if (fallback && fallback.payload) {
                if (fallback.payload.sender) {
                  fallback.payload.sender = fallback.payload.sender.replace('$sendername', originalSender)
                }
                if (fallback.payload.text) {
                  fallback.payload.text = fallback.payload.text.replace('$originalText', originalText)
                  fallback.payload.text = fallback.payload.text.replace('$sendername', originalSender)
                  fallback.payload.text = fallback.payload.text.replace('$specialNumbers', () => numbersToIcons(originalText))
                  fallback.payload.text = fallback.payload.text.replace('$textNumbers', () => numbersToText(originalText))

                  if (/(?:^| )\$\d+(?: |$)/i.test(fallback.payload.text)) {
                    fallback.payload.text = originalText.replace(await getTextReplaceRE(), fallback.payload.text)
                  }
                }
              }

              return fallback
            }))
          }
          break
        }
        case this.tagsMeta.buttons.tag: {
          if (sending.type === SENDING_TYPES.viber) {
            sending.payload.button = {
              text: value[0].text,
              url: value[0].action
            }
          } else {
            sending.payload.buttons = value
          }
          break
        }
      }
    }

    return sending
  }
}

module.exports = Actions
